/* eslint-disable no-console */
/* eslint-disable import/no-default-export */

/* eslint-env browser */

import React, { Children, useCallback, useEffect, useRef, useState } from 'react';
import { isObject, isEmpty } from 'lodash';
import { getEntries } from 'Client/session-storage-api/sessionStorageApi';
import { ABTestContext } from './ABTestContext';

const ABTEST_PREFIX = 'testAB.';
const ABTEST_EVENT = 'testAB';
const env = process.env.NODE_ENV;

export const useMockABTasty = () => {
  useEffect(() => {
    // Skip mock in test environment to prevent Jest hanging
    if (env === 'test') {
      return;
    }

    // Only run mock in localhost development environment
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (env !== 'development' || !isLocalhost) {
      return;
    }

    // expose the functions to the window object (only in localhost development)
    if (typeof window !== 'undefined' && env === 'development') {
      window.CM_AB_UTILS = {
        // turn on the mock
        enableMock: () => {
          localStorage.setItem('CM_ENABLE_AB_MOCK', 'true');
          console.info('CM_AB: Mock enabled - refresh page to apply');
        },
        // turn off the mock
        disableMock: () => {
          localStorage.removeItem('CM_ENABLE_AB_MOCK'); // Remove flag to disable (default)
          console.info('CM_AB: Mock disabled (default) - refresh page to apply');
        },
        // Force a specific value
        forceValue: (testId, value) => {
          if (window.forceABTestUpdate) {
            window.forceABTestUpdate({ [testId]: value });
            console.info(`CM_AB: Forced ${testId} to "${value}"`);
          }
        },
        // check current state
        getStatus: () => {
          const mockEnabled = localStorage.getItem('CM_ENABLE_AB_MOCK') === 'true'; // Default: false
          const sessionValue = sessionStorage.getItem('testAB.ab-test-activities');
          return {
            mockEnabled,
            sessionValue,
            hasABTasty: !!window.ABTasty,
            hasForceUpdate: !!window.forceABTestUpdate,
            hasBookingEngineUpdate: !!window.forceBookingEngineUpdate,
          };
        },
      };
      console.info('CM_AB: Utilities available on window.CM_AB_UTILS (localhost only)');
    }

    // Mock ABTasty disabled by default - developers can enable it for testing
    const enableMock = localStorage.getItem('CM_ENABLE_AB_MOCK') === 'true'; // Default: false

    if (!enableMock) {
      console.info(
        'CM_AB: Mock ABTasty disabled by default - use window.CM_AB_UTILS.enableMock() to activate',
      );
      return;
    }

    if (
      typeof window !== 'undefined' &&
      (!window.ABTasty || typeof window.ABTasty.getTestsOnPage !== 'function')
    ) {
      const mockDelay = 500; // 500 msecondes to simulate the delay

      console.info(`CM_AB: Mocking with ${mockDelay}ms delay - starting with '0'`);

      // initialize the mock with '0' (no test active)
      window.ABTasty = window.ABTasty || {};
      window.ABTasty.getTestsOnPage = () => ({
        1460669: {
          name: 'TEST - FR - Activities step - delete step',
          variationID: 1816698,
          variationName: 'Variation 1',
        },
      });

      // set the session storage with '0' (no test active)
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem('testAB.ab-test-activities', '0');
        console.info('CM_AB: Session storage initialized with "0"');
      }

      // Dispatch the official AB Tasty event to trigger the flow
      window.dispatchEvent(new Event('abtasty_tagContentExecuted'));
      console.info('CM_AB: Initial abtasty_tagContentExecuted event dispatched');

      // force the state ABTestProvider with '0' (no test active)
      if (window.forceABTestUpdate) {
        window.forceABTestUpdate({ 'ab-test-activities': '0' });
        console.info('CM_AB: Provider state updated with "0"');
      }

      // after the delay, switch to '1' (test active)
      const timeoutId = setTimeout(() => {
        console.info('CM_AB: Delay completed, switching to "1"');

        // update the session storage with '1' (test active)
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem('testAB.ab-test-activities', '1');
          console.info('CM_AB: Session storage updated to "1"');
        }

        // Dispatch the official AB Tasty event to trigger the flow with new data
        window.dispatchEvent(new Event('abtasty_tagContentExecuted'));
        console.info('CM_AB: abtasty_tagContentExecuted event dispatched with new data');

        // force the state ABTestProvider with '1' (test active)
        if (window.forceABTestUpdate) {
          window.forceABTestUpdate({ 'ab-test-activities': '1' });
          console.info('CM_AB: Provider state updated with "1"');
        }

        // force the BookingEngine update
        if (window.forceBookingEngineUpdate) {
          console.info('CM_AB: Calling forceBookingEngineUpdate');
          window.forceBookingEngineUpdate();
        }
      }, mockDelay);

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, []);
};

const isValidValues = (src, values) =>
  isObject(values) &&
  !isEmpty(values) &&
  !Object.entries(values).every(([key, value]) => src[key] === value);

// Session-storage fallback
const fetchABTestData = () => {
  return getEntries()
    .filter(([key]) => key.startsWith(ABTEST_PREFIX))
    .reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key.replace(ABTEST_PREFIX, '')]: value.toString(),
      }),
      {},
    );
};

// REAL AB Tasty configs
const AB_TESTS_CONFIG = [
  {
    testId: 1460669,
    contextKey: 'ab-test-activities',
    activeVariationIds: [1816698],
    description: 'TEST - FR - Activities step - delete step',
  },
  {
    testId: 1477415,
    contextKey: 'ab-test-activities-uk',
    activeVariationIds: [1838508],
    description: 'TEST - UK - Activities step - delete step',
  },
  {
    testId: 1477417,
    contextKey: 'ab-test-activities-br',
    activeVariationIds: [1838510],
    description: 'TEST - BR - Activities step - delete step',
  },
  {
    testId: 1477416,
    contextKey: 'ab-test-activities-ca',
    activeVariationIds: [1838509],
    description: 'TEST - CA - Activities step - delete step',
  },
  {
    testId: 1477418,
    contextKey: 'ab-test-activities-jp',
    activeVariationIds: [1838511],
    description: 'TEST - JP - Activities step - delete step',
  },
];

const fetchABTastyData = () => {
  console.info('CM_AB: fetchABTastyData called');

  try {
    if (
      typeof window !== 'undefined' &&
      window.ABTasty &&
      typeof window.ABTasty.getTestsOnPage === 'function'
    ) {
      const tests = window.ABTasty.getTestsOnPage();
      console.info('CM_AB: ABTasty.getTestsOnPage() returned:', tests);

      const abTastyData = {};

      AB_TESTS_CONFIG.forEach(({ testId, contextKey, activeVariationIds }) => {
        const test = tests[testId];
        if (test) {
          const isActive = activeVariationIds.includes(test.variationID);
          const value = isActive ? '1' : '0';
          abTastyData[contextKey] = value;
          console.info(`CM_AB: Test ${testId} (${contextKey}):`, {
            test,
            variationID: test.variationID,
            activeVariationIds,
            isActive,
            value,
          });
        } else {
          abTastyData[contextKey] = '0';
          console.info(`CM_AB: Test ${testId} (${contextKey}) not found, defaulting to "0"`);
        }
      });

      console.info('CM_AB: Final abTastyData:', abTastyData);
      return abTastyData;
    }
    console.info('CM_AB: ABTasty not available or getTestsOnPage not a function');
  } catch (error) {
    console.warn('CM_AB: Error fetching ABTasty data', error);
  }

  // Default fallback
  const fallbackData = AB_TESTS_CONFIG.reduce((acc, { contextKey }) => {
    acc[contextKey] = '0';
    return acc;
  }, {});
  console.info('CM_AB: Using fallback data:', fallbackData);
  return fallbackData;
};

// Helper function to get fallback data
const getFallbackData = () => {
  return AB_TESTS_CONFIG.reduce((acc, { contextKey }) => {
    acc[contextKey] = '0';
    return acc;
  }, {});
};

export default function ABTestProvider({ children }) {
  const [state, setState] = useState(() => {
    const sessionData = fetchABTestData();
    const fallbackData = getFallbackData();
    return {
      ...sessionData,
      abTastyData: fallbackData,
    };
  });

  const abTastyRetryTimerRef = useRef(null);
  const abTastyRetryCountRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const updateABTastyData = useCallback(() => {
    console.info('CM_AB: updateABTastyData called');

    const abTastyData = fetchABTastyData();
    console.info('CM_AB: fetchABTastyData result:', abTastyData);

    const abTastyTests = typeof window !== 'undefined' && window.ABTasty?.getTestsOnPage?.();
    const hasRealABTastyData =
      !isEmpty(abTastyData) &&
      typeof window !== 'undefined' &&
      window.ABTasty &&
      typeof window.ABTasty.getTestsOnPage === 'function' &&
      !isEmpty(abTastyTests);

    console.info('CM_AB: Data validation:', {
      abTastyData,
      abTastyTests,
      hasRealABTastyData,
      currentState: stateRef.current.abTastyData,
      eventState: typeof window !== 'undefined' && window.ABTasty?.eventState,
    });

    let finalData = null;

    if (hasRealABTastyData) {
      console.info('CM_AB: Real ABTasty data found');
      finalData = abTastyData;

      if (isValidValues(stateRef.current.abTastyData, abTastyData)) {
        console.info('CM_AB: Updating state with new ABTasty data');
        setState((prev) => ({ ...prev, abTastyData }));
      } else {
        console.info('CM_AB: ABTasty data unchanged, but still triggering event');
      }

      if (abTastyRetryTimerRef.current) {
        console.info('CM_AB: Stopping retry timer');
        clearTimeout(abTastyRetryTimerRef.current);
        abTastyRetryTimerRef.current = null;
      }
    } else {
      console.info('CM_AB: No real ABTasty data yet, using fallback data immediately');

      finalData = getFallbackData();

      if (isValidValues(stateRef.current.abTastyData, finalData)) {
        console.info('CM_AB: Setting fallback data immediately');
        setState((prev) => ({ ...prev, abTastyData: finalData }));
      }

      if (!abTastyRetryTimerRef.current && !abTastyRetryCountRef.current) {
        abTastyRetryCountRef.current = 1;
        console.info('CM_AB: Starting background retry mechanism (attempt 1/3)');
        abTastyRetryTimerRef.current = setTimeout(() => {
          abTastyRetryTimerRef.current = null;
          updateABTastyData();
        }, 1000);
      } else if (abTastyRetryCountRef.current && abTastyRetryCountRef.current < 3) {
        abTastyRetryCountRef.current++;
        console.info(`CM_AB: Background retry attempt ${abTastyRetryCountRef.current}/3`);
        abTastyRetryTimerRef.current = setTimeout(() => {
          abTastyRetryTimerRef.current = null;
          updateABTastyData();
        }, 1000);
      } else {
        console.info('CM_AB: Max retries reached, keeping fallback data');
      }
    }

    if (finalData && typeof document !== 'undefined') {
      document.dispatchEvent(
        new CustomEvent('abTastyDataUpdated', {
          detail: finalData,
        }),
      );
      console.info('CM_AB: abTastyDataUpdated event dispatched with data:', finalData);
    }
  }, []);

  useEffect(() => {
    const handleEvent = (e) => {
      if (isValidValues(stateRef.current, e.values)) {
        setState((prev) => ({ ...prev, ...e.values }));
      }
    };

    const handleABTastyReady = (event) => {
      console.info('CM_AB: ABTasty ready event received:', {
        eventType: event.type,
        timestamp: new Date().toISOString(),
        abTastyExists: typeof window !== 'undefined' && !!window.ABTasty,
        getTestsOnPage: typeof window !== 'undefined' && typeof window.ABTasty?.getTestsOnPage,
      });

      updateABTastyData();
    };

    document.addEventListener(ABTEST_EVENT, handleEvent, false);
    window.addEventListener('abtasty_tagContentExecuted', handleABTastyReady, false);
    document.addEventListener('cm_app_mounted', handleABTastyReady, false);

    console.info('CM_AB: ABTestProvider mounted, listening for events');
    console.info('CM_AB: Environment:', {
      NODE_ENV: process.env.NODE_ENV,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
      isLocalhost:
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'),
    });
    console.info('CM_AB: ABTasty status:', {
      exists: typeof window !== 'undefined' && !!window.ABTasty,
      eventState: typeof window !== 'undefined' && window.ABTasty?.eventState,
      tagContentExecuted:
        typeof window !== 'undefined' && window.ABTasty?.eventState?.tagContentExecuted?.status,
      getTestsOnPage: typeof window !== 'undefined' && typeof window.ABTasty?.getTestsOnPage,
    });

    if (
      typeof window !== 'undefined' &&
      window.ABTasty?.eventState?.tagContentExecuted?.status === 'complete'
    ) {
      console.info('CM_AB: ABTasty already ready, fetching data immediately');
      updateABTastyData();
    } else if (
      typeof window !== 'undefined' &&
      window.ABTasty &&
      typeof window.ABTasty.getTestsOnPage === 'function'
    ) {
      console.info('CM_AB: ABTasty exists but event not fired, attempting to fetch data');
      updateABTastyData();
    }

    if (typeof window !== 'undefined') {
      window.forceABTestUpdate = (data) => {
        console.info('CM_AB: forceABTestUpdate called with:', data);
        setState((prev) => ({ ...prev, abTastyData: data }));
      };

      window.CM_AB_DEBUG = {
        checkABTasty: () => {
          console.info('CM_AB: ABTasty Debug Info:', {
            exists: !!window.ABTasty,
            eventState: window.ABTasty?.eventState,
            getTestsOnPage: typeof window.ABTasty?.getTestsOnPage,
            tests: window.ABTasty?.getTestsOnPage?.(),
            tagContentExecuted: window.ABTasty?.eventState?.tagContentExecuted,
          });
        },
        triggerEvent: () => {
          console.info('CM_AB: Manually triggering abtasty_tagContentExecuted event');
          window.dispatchEvent(new Event('abtasty_tagContentExecuted'));
        },
        checkState: () => {
          console.info('CM_AB: Current Provider State:', stateRef.current);
        },
        forceUpdate: () => {
          console.info('CM_AB: Manually calling updateABTastyData');
          updateABTastyData();
        },
      };
    }

    if (typeof document !== 'undefined') {
      document.dispatchEvent(
        new CustomEvent('abTastyDataUpdated', {
          detail: stateRef.current.abTastyData,
        }),
      );
      console.info('CM_AB: Immediate abTastyDataUpdated event dispatched');
    }

    updateABTastyData();

    return () => {
      document.removeEventListener(ABTEST_EVENT, handleEvent, false);
      document.removeEventListener('cm_app_mounted', handleABTastyReady, false);
      window.removeEventListener('abtasty_tagContentExecuted', handleABTastyReady, false);

      if (abTastyRetryTimerRef.current) {
        clearTimeout(abTastyRetryTimerRef.current);
        abTastyRetryTimerRef.current = null;
      }

      if (typeof window !== 'undefined' && window.forceABTestUpdate) {
        delete window.forceABTestUpdate;
      }
    };
  }, [updateABTastyData]);

  return (
    <ABTestContext.Provider value={state}>
      {children ? Children.only(children) : null}
    </ABTestContext.Provider>
  );
}
